import { createElement, KeyboardEvent, memo, MouseEvent, ReactNode, useCallback, useState } from "react";
import classNames from "classnames";
import BaseModal from "react-modal";
import { Link, LinkProps, ProgressButton, ProgressButtonProps } from "../../ui";

import styles from "./style.module.scss";

export type ConfirmationComponentProps = {
  title?: string,
  onSubmit?: (result: boolean) => boolean | Promise<boolean>,
  submitText?: string,
  rejectText?: string,
  children?: ReactNode,
  highlightPosition?: "left" | "right",
  submitPosition?: "left" | "right",
  position?: "topRight" | "center",
  overlayClassName?: string,
  className?: string,
  buttonProps?: ProgressButtonProps,
  linkProps?: LinkProps,
} & Omit<BaseModal.Props, "overlayClassName" | "className">

const buttonProps: ProgressButtonProps = {
  buttonType: "secondary",
  buttonColorSchema: "outlined",
  autoFocus: true,
};

const linkProps: LinkProps = {
  linkType: "m",
  component: "button",
};

export const Confirmation = memo<ConfirmationComponentProps>((
  {
    className,
    title,
    onRequestClose,
    submitText = "Yes",
    rejectText = "No",
    onSubmit = () => true,
    overlayClassName,
    children,
    highlightPosition = "right",
    submitPosition = "left",
    position = "topRight",
    buttonProps: additionalButtonProps,
    linkProps: additionalLinkProps,
    shouldCloseOnEsc = false,
    shouldCloseOnOverlayClick = false,
    ...rest
  }) => {

  const [ isSubmitting, setIsSubmitting ] = useState(false);

  const onSubmitClick = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    try {
      setIsSubmitting(true);
      if (await onSubmit?.(true)) {
        onRequestClose?.(e);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [ onSubmit, onRequestClose, setIsSubmitting ]);

  const onRejectClick = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    try {
      setIsSubmitting(true);
      if (await onSubmit?.(false)) {
        onRequestClose?.(e);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [ onSubmit, onRequestClose, setIsSubmitting ]);

  const submitProps = {
    onClick: onSubmitClick,
    children: submitText,
    type: "submit",
  };

  const rejectProps = {
    onClick: onRejectClick,
    children: rejectText,
  };

  return (
    <BaseModal
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      {...rest}
      overlayClassName={classNames(styles.overlay, overlayClassName, {
        [styles.overlayTopRight]: position === "topRight",
        [styles.overlayCenter]: position === "center",
      })}
      className={classNames(className, styles.root)}
      onRequestClose={onRequestClose}
    >
      {
        title &&
        <div className={styles.header}>
          {title}
        </div>
      }
      <div className={styles.content}>
        {children}
      </div>
      <div className={styles.buttons}>
        {
          createElement(
            highlightPosition === "left" ? ProgressButton : Link,
            {
              ...(highlightPosition === "left" ? { ...buttonProps, ...additionalButtonProps } : { ...linkProps, ...additionalLinkProps }),
              ...(submitPosition === "left" ? submitProps : rejectProps),
              disabled: isSubmitting,
            },
          )
        }
        {
          createElement(
            highlightPosition === "right" ? ProgressButton : Link,
            {
              ...(highlightPosition === "right" ? { ...buttonProps, ...additionalButtonProps } : { ...linkProps, ...additionalLinkProps }),
              ...(submitPosition === "right" ? submitProps : rejectProps),
              disabled: isSubmitting,
            },
          )
        }
      </div>
    </BaseModal>
  );
});

export default Confirmation;
